import { assertNever } from 'owa-assert';
import { mutator } from 'satcheljs';
import { setPanesSize } from '../actions/setPaneDisplayActions';
import { getHideReadingPaneOption } from '../selectors/getSxSOptions';
import SxSViewState, { RightPaneDisplay } from '../store/schema/SxSViewState';
import { AutoHideEmailEvent } from '../utils/eventNames';
import {
    MIN_PREVIEW_PANE_WIDTH,
    PREVIEW_PANE_WIDTH,
    RESPONSIVE_READING_PANE_WIDTH,
} from '../utils/sxsConstants';

const setPanesSizeMutatorFunction = (
    viewState: SxSViewState,
    leftPaneDimension: number,
    rightPaneDimension: number
) => {
    const minLeftPaneWidth = viewState.minLeftPaneWidth || MIN_PREVIEW_PANE_WIDTH;
    const minRightPaneWidth = viewState.minRightPaneWidth || RESPONSIVE_READING_PANE_WIDTH;

    const totalDimension = leftPaneDimension + rightPaneDimension;
    const totalDimensionRequiredToDisplayRightPane =
        PREVIEW_PANE_WIDTH + RESPONSIVE_READING_PANE_WIDTH;
    const haveSpaceForRightPane = totalDimension >= totalDimensionRequiredToDisplayRightPane;

    if (
        viewState.rightPaneDisplay === undefined ||
        viewState.rightPaneDisplay === RightPaneDisplay.Initial
    ) {
        viewState.rightPaneDisplay = getHideReadingPaneOption()
            ? RightPaneDisplay.Hide
            : RightPaneDisplay.Adaptive;
    }

    switch (viewState.rightPaneDisplay) {
        case RightPaneDisplay.Adaptive:
            if (haveSpaceForRightPane) {
                showRightPane();
            } else {
                hideRightPane(true);
            }
            break;
        case RightPaneDisplay.AlwaysHide:
        case RightPaneDisplay.Hide:
            hideRightPane();
            break;
        case RightPaneDisplay.Show:
            showRightPane();
            break;
        default:
            assertNever(viewState.rightPaneDisplay);
    }

    function hideRightPane(autoHiding: boolean = false) {
        viewState.hideRightPane = true;
        viewState.rightPaneSize = 0; // this will reset the size to default next time user clicks on show email
        viewState.leftPaneSizePixels = totalDimension;
        viewState.rightPaneSizePixels = 0;

        if (autoHiding) {
            viewState.didAutoHideRightPane = true;
            viewState.eventManager.emit(AutoHideEmailEvent);
        }
    }

    function showRightPane() {
        if (
            leftPaneDimension > minLeftPaneWidth &&
            Math.round(rightPaneDimension) < minRightPaneWidth
        ) {
            rightPaneDimension = minRightPaneWidth;
            leftPaneDimension = totalDimension - rightPaneDimension;
        }

        viewState.hideRightPane = false;
        const rightPaneSize = (100 * rightPaneDimension) / (rightPaneDimension + leftPaneDimension);
        viewState.rightPaneSize = rightPaneSize;
        viewState.leftPaneSizePixels = leftPaneDimension;
        viewState.rightPaneSizePixels = rightPaneDimension;
    }
};

mutator(setPanesSize, actionMessage => {
    const { viewState, leftPaneDimension, rightPaneDimension } = actionMessage;
    setPanesSizeMutatorFunction(viewState, leftPaneDimension, rightPaneDimension);
});

export { setPanesSizeMutatorFunction }; // exported for testing
